import React, { createContext, useContext, useEffect, useState } from 'react'
import { Session } from '@supabase/supabase-js'
import { supabase } from '../api/supabase'
import { User } from '../types'
import { debug } from '../../lib/utils/debug'

interface AuthContextType {
  session: Session | null
  user: User | null
  signUp: (email: string, password: string, name?: string) => Promise<void>
  signIn: (email: string, password: string) => Promise<void>
  signInWithGoogle: () => Promise<void>
  signOut: () => Promise<void>
  loading: boolean
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [session, setSession] = useState<Session | null>(null)
  const [user, setUser] = useState<User | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    debug.log('🔐 AuthContext mounting...')
    
    // Get session on mount
    supabase.auth.getSession().then(({ data: { session }, error }) => {
      debug.log('🔐 Initial session check:', { 
        hasSession: !!session, 
        userId: session?.user?.id,
        error 
      })
      
      setSession(session)
      if (session?.user) {
        // Create a basic user object immediately
        const basicUser: User = {
          id: session.user.id,
          email: session.user.email || '',
          name: session.user.user_metadata?.full_name || 
                session.user.user_metadata?.name || 
                session.user.email?.split('@')[0] || 
                'User',
          avatar_url: session.user.user_metadata?.avatar_url || '',
          streak_count: 0,
          role: 'member',
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        }
        setUser(basicUser)
        
        // Try to get the full user profile from database
        supabase
          .from('users')
          .select('*')
          .eq('id', session.user.id)
          .single()
          .then(({ data, error }) => {
            if (data && !error) {
              setUser(data)
            } else if (error?.code === 'PGRST116') {
              // User doesn't exist in database, create it
              supabase
                .from('users')
                .insert(basicUser)
                .select()
                .single()
                .then(({ data: newUser }) => {
                  if (newUser) setUser(newUser)
                })
            }
          })
      }
      setLoading(false)
    })

    // Listen for changes
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, session) => {
      debug.log('🔐 Auth state changed:', { 
        event, 
        hasSession: !!session, 
        userId: session?.user?.id 
      })
      
      setSession(session)
      if (session?.user) {
        // Create a basic user object immediately
        const basicUser: User = {
          id: session.user.id,
          email: session.user.email || '',
          name: session.user.user_metadata?.full_name || 
                session.user.user_metadata?.name || 
                session.user.email?.split('@')[0] || 
                'User',
          avatar_url: session.user.user_metadata?.avatar_url || '',
          streak_count: 0,
          role: 'member',
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        }
        setUser(basicUser)
        
        // Try to get the full user profile from database
        supabase
          .from('users')
          .select('*')
          .eq('id', session.user.id)
          .single()
          .then(({ data, error }) => {
            if (data && !error) {
              setUser(data)
            } else if (error?.code === 'PGRST116') {
              // User doesn't exist in database, create it
              supabase
                .from('users')
                .insert(basicUser)
                .select()
                .single()
                .then(({ data: newUser }) => {
                  if (newUser) setUser(newUser)
                })
            }
          })
      } else {
        setUser(null)
      }
      setLoading(false)
    })

    return () => subscription.unsubscribe()
  }, [])

  const signUp = async (email: string, password: string, name?: string) => {
    const { error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: {
          full_name: name,
        },
      },
    })
    if (error) throw error
  }

  const signIn = async (email: string, password: string) => {
    debug.log('🔐 AuthContext: Attempting sign in for email:', email)
    
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password,
    })
    
    if (error) {
      debug.error('🔐 AuthContext: Sign in error:', error)
      debug.error('🔐 AuthContext: Error details:', {
        message: error.message,
        status: error.status,
        code: error.code
      })
      throw error
    }
    
    debug.log('🔐 AuthContext: Sign in successful, session:', data.session?.user?.email)
  }

  const signInWithGoogle = async () => {
    const redirectTo = `${window.location.origin}/auth/callback`
    debug.log('🔐 Initiating Google OAuth with redirect to:', redirectTo)
    debug.log('🔐 Current URL:', window.location.href)
    debug.log('🔐 Current origin:', window.location.origin)
    
    // Check if there are any OAuth parameters in the current URL
    const urlParams = new URLSearchParams(window.location.search)
    const hashParams = new URLSearchParams(window.location.hash.substring(1))
    
    debug.log('🔐 URL search params:', Object.fromEntries(urlParams))
    debug.log('🔐 URL hash params:', Object.fromEntries(hashParams))
    
    const { data, error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: {
        redirectTo,
        queryParams: {
          access_type: 'offline',
          prompt: 'consent',
        },
      },
    })
    
    if (error) {
      debug.error('❌ Google OAuth error:', error)
      throw error
    }
    
    debug.log('✅ OAuth initiated:', data)
    debug.log('🔐 OAuth URL:', data?.url)
  }

  const signOut = async () => {
    const { error } = await supabase.auth.signOut()
    if (error) throw error
    setUser(null)
    setSession(null)
  }



  const value = {
    session,
    user,
    signUp,
    signIn,
    signInWithGoogle,
    signOut,
    loading,
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth() {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}